import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

// Types
interface Project {
  id: string;
  name: string;
  description: string | null;
  status: string;
  workspaceId: string;
  calendarId: string | null;
  startDate: string | null;
  finishDate: string | null;
  createdAt: string;
  updatedAt: string;
  _count?: {
    sheets: number;
    tasks: number;
  };
}

interface ProjectMemberUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  avatarUrl: string | null;
}

interface ProjectMember {
  projectId: string;
  userId: string;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  canEdit: boolean;
  canComment: boolean;
  canShare: boolean;
  user: ProjectMemberUser;
}

interface CreateProjectDto {
  name: string;
  description?: string;
  workspaceId?: string;
}

interface UpdateProjectDto {
  name?: string;
  description?: string;
  status?: string;
}

interface AddProjectMemberDto {
  userId: string;
  role?: 'owner' | 'admin' | 'member' | 'viewer';
  canEdit?: boolean;
  canComment?: boolean;
  canShare?: boolean;
}

// Query keys
export const projectKeys = {
  all: ['projects'] as const,
  lists: () => [...projectKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) => [...projectKeys.lists(), filters] as const,
  detail: (id: string) => [...projectKeys.all, 'detail', id] as const,
  members: (id: string) => [...projectKeys.detail(id), 'members'] as const,
};

function extractProjectsFromResponse(payload: unknown): Project[] {
  if (Array.isArray(payload)) {
    return payload as Project[];
  }

  if (payload && typeof payload === 'object') {
    const obj = payload as Record<string, unknown>;

    if (Array.isArray(obj.data)) {
      return obj.data as Project[];
    }

    if (obj.data && typeof obj.data === 'object') {
      const nested = obj.data as Record<string, unknown>;
      if (Array.isArray(nested.data)) {
        return nested.data as Project[];
      }
    }

    if (Array.isArray(obj.projects)) {
      return obj.projects as Project[];
    }
  }

  return [];
}

// Fetch all projects
export function useProjectsQuery() {
  return useQuery({
    queryKey: projectKeys.lists(),
    queryFn: async () => {
      const response = await api.get<any>('/projects');
      return extractProjectsFromResponse(response.data);
    },
  });
}

// Fetch single project
export function useProjectQuery(projectId: string) {
  return useQuery({
    queryKey: projectKeys.detail(projectId),
    queryFn: async () => {
      const response = await api.get<Project>(`/projects/${projectId}`);
      return response.data;
    },
    enabled: !!projectId,
  });
}

// Create project
export function useCreateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (dto: CreateProjectDto) => {
      const payload = dto.workspaceId ? dto : { ...dto, workspaceId: undefined };
      const response = await api.post<Project>('/projects', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
    },
  });
}

// Update project
export function useUpdateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, updates }: { projectId: string; updates: UpdateProjectDto }) => {
      const response = await api.patch<Project>(`/projects/${projectId}`, updates);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(data.id) });
    },
  });
}

// Delete project
export function useDeleteProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (projectId: string) => {
      await api.delete(`/projects/${projectId}`);
      return projectId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
    },
  });
}

// Fetch project members
export function useProjectMembersQuery(projectId: string) {
  return useQuery({
    queryKey: projectKeys.members(projectId),
    queryFn: async () => {
      const response = await api.get<ProjectMember[]>(`/projects/${projectId}/members`);
      return response.data;
    },
    enabled: !!projectId,
    staleTime: 60000,
  });
}

// Add project member
export function useAddProjectMember(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (dto: AddProjectMemberDto) => {
      const response = await api.post<ProjectMember>(`/projects/${projectId}/members`, dto);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.members(projectId) });
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
    },
  });
}

// Remove project member
export function useRemoveProjectMember(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (memberUserId: string) => {
      await api.delete(`/projects/${projectId}/members/${memberUserId}`);
      return memberUserId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.members(projectId) });
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
    },
  });
}

export type { Project, CreateProjectDto, UpdateProjectDto, ProjectMember, AddProjectMemberDto };
